import React, { createContext, useContext, useState, useEffect } from "react";


type Language = "en" | "fa"; // Add more languages if needed

// Define the context shape
interface TranslationContextType {
  language: string;
  changeLanguage: (lang: string) => void;
  t: (key: string) => string;
  isRTL: boolean;
}

// Create a context with default values
const TranslationContext = createContext<TranslationContextType>({
  language: "en",
  changeLanguage: () => {},
  t: (key) => key,
  isRTL: false,
});

// List of RTL languages
const RTL_LANGUAGES = ["fa", "ar", "he", "ur"];

// Translation Provider Component
export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // const [language, setLanguage] = useState<Language>(localStorage.getItem("language") || "en");
  const [language, setLanguage] = useState<Language | string>(localStorage.getItem("language") || "en");
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const isRTL = RTL_LANGUAGES.includes(language);

  // Load translation JSON dynamically
  useEffect(() => {
    loadTranslations(language);
  }, [language]);

  useEffect(()=> {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  }, [isRTL])

  // Change language function
  const changeLanguage = (lang: string) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
  };

  const loadTranslations = async (lang: Language | string) => {
    try {
      const response = await fetch(`/locales/${lang}.json`);
      const data = await response.json();
      setTranslations(data);
    } catch (error) {
      console.error("Error loading translation file:", error);
    }
  };

  // Translation function
  const t = (key: string) => translations[key] || key;

  return (
    <TranslationContext.Provider value={{ language, changeLanguage, t, isRTL }}>
      {children}
    </TranslationContext.Provider>
  );
};

// Custom hook to use translation
export default function useTranslation() {
  return useContext(TranslationContext);
}
